import { useEffect, useMemo } from 'react'
import { CssVarsProvider, useColorScheme as useJoyColorScheme } from '@mui/joy/styles'
import {
    extendTheme as materialExtendTheme,
    CssVarsProvider as MaterialCssVarsProvider,
    THEME_ID as MATERIAL_THEME_ID,
    useColorScheme as useMaterialColorScheme,
} from '@mui/material/styles'
import { densityTheme } from '@/theme/density'

/**
 * Mirrors Joy's color scheme onto Material's.
 *
 * Joy and Material each own an INDEPENDENT mode: separate React contexts and separate
 * localStorage keys (`joy-mode` vs `mui-mode`). Only Joy's is wired to the in-app toggle
 * (`ColorSchemeToggle` → Joy `setMode`, which also drives Tailwind's `<html>.dark`), and
 * Material's `CssVarsProvider` defaults to `defaultMode: 'system'` — so before this sync
 * every `@mui/material` surface (Dialog, Menu, Popover, Tooltip…) followed the OPERATING
 * SYSTEM theme rather than the app toggle. With OS light + app dark that renders a white
 * Dialog behind `dark:`-styled Tailwind content (white-on-white text); with OS dark + app
 * light, the inverse. Pushing Joy's mode into Material makes one toggle govern everything.
 */
const MaterialColorSchemeSync = () => {
    const { mode: joyMode } = useJoyColorScheme()
    const { mode: materialMode, setMode: setMaterialMode } = useMaterialColorScheme()

    useEffect(() => {
        // Joy reports `undefined` until it has hydrated from storage; writing then would
        // clobber Material with a value Joy is about to replace.
        if (!joyMode || joyMode === materialMode) return
        setMaterialMode(joyMode)
    }, [joyMode, materialMode, setMaterialMode])

    return null
}

/**
 * The MUI Material + Joy CssVars provider pair the dashboard mounts in MasterLayout. Reuse it to wrap any
 * subtree rendered OUTSIDE MasterLayout — e.g. an app-root portal such as the Box Truck bid notifications —
 * that mounts Joy/Material components calling `useColorScheme`. Without a CssVarsProvider ancestor those throw
 * "MUI: `useColorScheme` must be called under <CssVarsProvider />" (the crash on opening a bid card's drawer).
 */
export const AppThemeProviders = ({ children }: { children: React.ReactNode }) => {
    const materialTheme = useMemo(() => materialExtendTheme(), [])
    return (
        <MaterialCssVarsProvider theme={{ [MATERIAL_THEME_ID]: materialTheme }}>
            <CssVarsProvider disableTransitionOnChange theme={densityTheme}>
                <MaterialColorSchemeSync />
                {children}
            </CssVarsProvider>
        </MaterialCssVarsProvider>
    )
}
